import { useMemo, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";

import { PageHeader } from "../../layout/secure/common";
import {
  approveManualWordPressInstallation,
  messageFromError,
} from "../../utils/api";

const normalizeInstallationCode = value => String(value || "")
  .toUpperCase()
  .replace(/[^A-Z2-9]/g, "")
  .slice(0, 10);

function WordPressManualConnect() {
  const initialCode = useMemo(() => {
    const query = new URLSearchParams(window.location.search);
    return normalizeInstallationCode(query.get("code"));
  }, []);
  const [code, setCode] = useState(initialCode);
  const [submitting, setSubmitting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState("");

  const approve = async event => {
    event.preventDefault();
    if (code.length !== 10) {
      setError("Enter the complete ten-character installation code.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await approveManualWordPressInstallation(code);
      setConnected(true);
    } catch (requestError) {
      setError(messageFromError(
        requestError,
        "The one-time installation code could not be approved."
      ));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Approve WordPress manually"
        description="Use this fallback only when the normal WordPress authorization redirect is blocked by the host."
      />
      <section className="fera-panel fera-wordpress-connect" aria-labelledby="wordpress-manual-title">
        <div className="fera-panel-heading">
          <div>
            <p className="fera-eyebrow">FeraSurvey integration</p>
            <h2 id="wordpress-manual-title">One-time installation code</h2>
          </div>
          <span className={`badge ${connected ? "text-bg-success" : "text-bg-warning"}`}>
            {connected ? "Approved" : "Approval required"}
          </span>
        </div>
        {error ? <Alert variant="danger">{error}</Alert> : null}
        {connected ? (
          <Alert variant="success">
            This WordPress installation is approved. Return to WordPress and select
            <strong> I approved it - check status</strong>. The one-time code cannot be reused.
          </Alert>
        ) : (
          <Form onSubmit={approve}>
            <Form.Group controlId="wordpress-installation-code">
              <Form.Label>Installation code</Form.Label>
              <Form.Control
                value={code}
                onChange={event => setCode(normalizeInstallationCode(event.target.value))}
                autoComplete="one-time-code"
                inputMode="text"
                maxLength={10}
                placeholder="ABCDE23456"
                aria-describedby="wordpress-code-help"
              />
              <Form.Text id="wordpress-code-help">
                The code is displayed under Settings -&gt; FeraSurvey in WordPress
                and expires after ten minutes.
              </Form.Text>
            </Form.Group>
            <div className="fera-page-buttons mt-3">
              <Button type="submit" disabled={submitting || code.length !== 10}>
                {submitting ? "Approving\u2026" : "Approve WordPress installation"}
              </Button>
            </div>
          </Form>
        )}
        <p className="text-muted mb-0">
          Approval grants only installation status, published-survey metadata,
          embed metadata, and usage access. It does not grant access to responses,
          users, billing changes, or survey editing.
        </p>
      </section>
    </>
  );
}

export { normalizeInstallationCode };

export default WordPressManualConnect;
